import { useSystemKeyStatus } from '@/entities/system-key'
import userService from '@/features/user/service'
import { SYSTEM_KEY } from '@/shared/config'
import { formatDate } from '@/shared/lib/format'
import { storage } from '@/shared/lib/storage'
import { getRegexPhoneNumber } from '@/shared/lib/strings-utils'
import { useAppDispatch } from '@/shared/redux'
import { setUserInfo } from '@/shared/redux'
import { UiCustomModal } from '@/shared/ui'
import { CameraOutlined as Camera, LoadingOutlined, SaveOutlined as Save } from '@/shared/ui/icons'
import UiButton from '@/shared/ui/ui-button'
import { UiCol, UiRow } from '@/shared/ui/ui-grid'
import { notify } from '@/shared/ui/ui-notification'
import UiSelect from '@/shared/ui/ui-select'
import { PhantomSkeleton } from '@/shared/ui/ui-skeleton'
import UiTag from '@/shared/ui/ui-tag'
import { useQuery, useQueryClient } from '@tanstack/react-query'
// ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
import { Form, Input, Upload } from 'antd'
// ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
import type { UploadProps } from 'antd'
import { useState } from 'react'

// Viền cầu vồng trang trí quanh avatar, mang nghĩa cố định không đổi theo theme
// ds-guard-ignore: gradient trang trí viền avatar
const AVATAR_RING_DRAG = 'linear-gradient(135deg, #10b981, #3b82f6, #8b5cf6)'
// ds-guard-ignore: gradient trang trí viền avatar
const AVATAR_RING = 'linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6, #3b82f6, #10b981)'

const profileQuery = { queryKey: ['profile'] as const, queryFn: userService.getProfile }

interface UserProfileModalProps {
  open: boolean
  onClose: () => void
}

interface FormValues {
  fullName: string
  email: string
  phoneNumber?: string
  address?: string
}

export default function UserProfileModal({ open, onClose }: Readonly<UserProfileModalProps>) {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { data, isFetching } = useQuery({ ...profileQuery, enabled: open })
  const profile = data?.object
  const userStatus = useSystemKeyStatus(SYSTEM_KEY.USER_STATUS)
  const [submitting, setSubmitting] = useState(false)
  const [submittingAvatar, setSubmittingAvatar] = useState(false)
  // Ảnh xem trước khi chọn file; null = dùng avatar của hồ sơ
  const [previewAvatar, setPreviewAvatar] = useState<string | null>(null)
  const currentAvatar = previewAvatar ?? profile?.avatar ?? ''
  const [isDragOver, setIsDragOver] = useState(false)
  const [hasFormError, setHasFormError] = useState(false)
  const [form] = Form.useForm<FormValues>()

  // Tải bản mới nhất vào cache, tránh ghi đè phần user đang sửa trong form
  const refreshProfile = async () => {
    const res = await queryClient.fetchQuery({ ...profileQuery, staleTime: 0 })
    return res?.object
  }

  const uploadAvatar = async (file: File) => {
    try {
      setSubmittingAvatar(true)
      const res = await userService.updateProfileAvatar(file)
      if (!res?.isOk) {
        notify.error((res as any)?.description || (res as any)?.message || 'Cập nhật ảnh thất bại')
        setPreviewAvatar(null)
        return
      }

      notify.success('Cập nhật ảnh đại diện thành công')
      const next = await refreshProfile()
      if (next) {
        setPreviewAvatar(null)
        const savedUserInfo = storage.getUserInfo() || {}
        const nextUserInfo = { ...savedUserInfo, avatar: next.avatar || '' }
        storage.setUserInfo(nextUserInfo)
        dispatch(setUserInfo(nextUserInfo))
      }
    } catch {
      setPreviewAvatar(null)
    } finally {
      setSubmittingAvatar(false)
    }
  }

  const processAvatarFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const preview = e.target?.result as string
      if (preview) {
        setPreviewAvatar(preview)
      }
      uploadAvatar(file)
    }
    reader.readAsDataURL(file)
  }

  const handleSelectAvatar: UploadProps['beforeUpload'] = (file) => {
    processAvatarFile(file)
    return false
  }

  // Đóng modal: bỏ ảnh xem trước và đưa form về dữ liệu hồ sơ mới nhất
  const handleClose = () => {
    setPreviewAvatar(null)
    form.resetFields()
    onClose()
  }

  const handleSaveProfile = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      const res = await userService.updateProfile({
        fullName: values.fullName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        address: values.address,
      })
      if (res?.isOk) {
        notify.success('Cập nhật thông tin cá nhân thành công')
        // Tải lại profile mới nhất từ BE (đã có avatar URL đầy đủ)
        const next = await refreshProfile()
        if (next) {
          setPreviewAvatar(null)
          const savedUserInfo = storage.getUserInfo() || {}
          const nextUserInfo = {
            ...savedUserInfo,
            fullName: next.fullName,
            email: next.email,
            avatar: next.avatar || '',
          }
          storage.setUserInfo(nextUserInfo)
          dispatch(setUserInfo(nextUserInfo))
        }
        onClose()
      }
    } catch {
      // Lỗi API đã được Axios Interceptor tự động hiển thị toast, lỗi form validation hiển thị inline
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <UiCustomModal
      title="Thông tin tài khoản"
      open={open}
      onCancel={handleClose}
      size="md"
      footer={[
        <UiButton key="cancel" onClick={handleClose}>
          Hủy
        </UiButton>,
        <UiButton
          key="save"
          type="primary"
          loading={submitting}
          disabled={hasFormError || !profile}
          onClick={handleSaveProfile}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Save size={15} /> Lưu thay đổi
        </UiButton>,
      ]}
    >
      {/* .ant-modal-body bị _antd-escapes.scss ép padding 0 !important → tự đệm ở đây */}
      <div style={{ padding: 'var(--vbdt-space-xs) var(--vbdt-space-md)' }}>
        {isFetching && !profile && <PhantomSkeleton kind="form" />}
        <div hidden={isFetching && !profile}>
          {/* Avatar Section với hiệu ứng viền cầu vồng hiện đại & hỗ trợ Kéo thả (Drag & Drop) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: 'var(--vbdt-space-lg)',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                borderRadius: '50%',
                padding: 2,
                background: isDragOver ? AVATAR_RING_DRAG : AVATAR_RING,
                boxShadow: isDragOver
                  ? '0 0 0 3px color-mix(in srgb, var(--vbdt-color-accent-base) 25%, transparent)'
                  : 'var(--vbdt-shadow-sm)',
                marginBottom: 'var(--vbdt-space-sm)',
                transition: 'all 0.2s ease-in-out',
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragOver(false)
                const file = e.dataTransfer.files?.[0]
                if (file?.type.startsWith('image/')) {
                  processAvatarFile(file)
                }
              }}
            >
              <Upload
                showUploadList={false}
                beforeUpload={handleSelectAvatar}
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                style={{ display: 'block', width: '100%', height: '100%' }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={currentAvatar || profile?.avatar || '/user-holder.svg'}
                    onError={(e) => {
                      const target = e.currentTarget
                      if (!target.src.endsWith('/user-holder.svg')) {
                        target.src = '/user-holder.svg'
                      }
                    }}
                    alt="Avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      backgroundColor: 'var(--vbdt-color-bg-surface)',
                      display: 'block',
                    }}
                  />

                  {/* Lớp phủ hover / drag over */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: 'var(--vbdt-color-bg-overlay)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--vbdt-color-text-on-accent)',
                      opacity: isDragOver ? 1 : 0,
                      transition: 'opacity 0.2s ease-in-out',
                      fontSize: 10,
                      fontWeight: 500,
                      gap: 2,
                    }}
                    onMouseEnter={(e) => {
                      if (!isDragOver) e.currentTarget.style.opacity = '1'
                    }}
                    onMouseLeave={(e) => {
                      if (!isDragOver) e.currentTarget.style.opacity = '0'
                    }}
                  >
                    <Camera size={16} />
                    <span>{isDragOver ? 'Thả ảnh' : 'Đổi ảnh'}</span>
                  </div>

                  {/* Overlay loading đè lên ảnh trong lúc đang tải ảnh đại diện lên */}
                  {submittingAvatar ? (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        backgroundColor: 'var(--vbdt-color-bg-overlay)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LoadingOutlined className="animate-spin" />
                    </div>
                  ) : null}
                </div>
              </Upload>

              {/* Huy hiệu Camera nổi ở góc dưới */}
              <div
                title="Click hoặc kéo thả ảnh để đổi avatar"
                style={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'var(--vbdt-color-accent-base)',
                  color: 'var(--vbdt-color-text-on-accent)',
                  border: '2px solid var(--vbdt-color-bg-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'var(--vbdt-shadow-md)',
                  pointerEvents: 'none',
                }}
              >
                <Camera size={11} />
              </div>
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--vbdt-color-text-primary)',
              }}
            >
              {profile?.fullName || profile?.userName || 'Người dùng'}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--vbdt-color-text-secondary)',
                marginTop: 2,
              }}
            >
              @{profile?.userName}
            </div>
            <div
              style={{
                marginTop: 'var(--vbdt-space-xs)',
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              {profile?.isSuperAdmin ? (
                <UiTag color="red">Quản trị viên tối cao</UiTag>
              ) : profile?.roleNames && profile.roleNames.length > 0 ? (
                profile.roleNames.map((role) => (
                  <UiTag color="blue" key={role}>
                    {role}
                  </UiTag>
                ))
              ) : (
                <UiTag color="blue">{profile?.roleName || 'Người dùng'}</UiTag>
              )}
              <UiTag color={userStatus.getColor(profile?.status)}>
                {userStatus.getLabel(profile?.status)}
              </UiTag>
            </div>
          </div>

          {/* Form Chỉnh sửa thông tin tài khoản trực tiếp */}
          {/* Mount khi hồ sơ về lần đầu: initialValues chỉ nạp một lần, refetch sau đó không ghi đè phần đang sửa */}
          {profile ? (
            <Form
              form={form}
              initialValues={{
                fullName: profile?.fullName || '',
                email: profile?.email || '',
                phoneNumber: profile?.phoneNumber || '',
                address: profile?.address || '',
              }}
              layout="vertical"
              onFieldsChange={() => {
                setHasFormError(form.getFieldsError().some(({ errors }) => errors.length > 0))
              }}
            >
              <UiRow gutter={16}>
                <UiCol span={12}>
                  <Form.Item label="Tên đăng nhập">
                    <Input value={profile?.userName} disabled />
                  </Form.Item>
                </UiCol>
                <UiCol span={12}>
                  <Form.Item label="Nhóm quyền">
                    <Input
                      value={
                        (profile?.roleNames && profile.roleNames.length > 0
                          ? profile.roleNames.join(', ')
                          : profile?.roleName) ||
                        (profile?.isSuperAdmin ? 'Quản trị viên tối cao' : 'Người dùng')
                      }
                      disabled
                    />
                  </Form.Item>
                </UiCol>
                <UiCol span={24}>
                  <Form.Item label="Phòng ban / Chức vụ">
                    {(profile?.departmentPositions ?? []).length > 0 ? (
                      (profile?.departmentPositions ?? []).map((dp, index) => (
                        <UiRow
                          gutter={8}
                          justify="space-between"
                          align="middle"
                          wrap={false}
                          key={`${dp.departmentID ?? ''}-${dp.positionID ?? ''}-${index}`}
                          style={{ marginBottom: 'var(--vbdt-space-xs)' }}
                        >
                          <UiCol flex={1}>
                            <UiSelect
                              disabled
                              placeholder="Phòng ban"
                              value={dp.departmentID}
                              options={
                                dp.departmentID
                                  ? [{ value: dp.departmentID, label: dp.departmentName ?? '' }]
                                  : []
                              }
                            />
                          </UiCol>
                          <UiCol flex={1}>
                            <UiSelect
                              disabled
                              placeholder="Chức vụ"
                              value={dp.positionID}
                              options={
                                dp.positionID
                                  ? [{ value: dp.positionID, label: dp.positionName ?? '' }]
                                  : []
                              }
                            />
                          </UiCol>
                        </UiRow>
                      ))
                    ) : (
                      <Input value="Chưa được gán" disabled />
                    )}
                  </Form.Item>
                </UiCol>
                {profile?.representativeName ? (
                  <UiCol span={12}>
                    <Form.Item label="Người đại diện">
                      <Input value={profile.representativeName} disabled />
                    </Form.Item>
                  </UiCol>
                ) : null}
                <UiCol span={12}>
                  <Form.Item label="Ngày tạo">
                    <Input value={formatDate(profile?.createdDate)} disabled />
                  </Form.Item>
                </UiCol>
                <UiCol span={12}>
                  <Form.Item
                    label="Họ và tên"
                    name="fullName"
                    rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
                  >
                    <Input placeholder="Nhập họ và tên" />
                  </Form.Item>
                </UiCol>
                <UiCol span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Vui lòng nhập email' },
                      { type: 'email', message: 'Email không hợp lệ' },
                    ]}
                  >
                    <Input placeholder="Nhập địa chỉ email" />
                  </Form.Item>
                </UiCol>
                <UiCol span={12}>
                  <Form.Item
                    label="Số điện thoại"
                    name="phoneNumber"
                    rules={[
                      {
                        pattern: getRegexPhoneNumber(),
                        message: 'Số điện thoại không hợp lệ',
                      },
                    ]}
                  >
                    <Input placeholder="Nhập số điện thoại" />
                  </Form.Item>
                </UiCol>
                <UiCol span={12}>
                  <Form.Item label="Địa chỉ" name="address">
                    <Input placeholder="Nhập địa chỉ liên hệ" />
                  </Form.Item>
                </UiCol>
              </UiRow>
            </Form>
          ) : null}
        </div>
      </div>
    </UiCustomModal>
  )
}
